import { z } from "zod"
import { includes, uniq } from "lodash"
import { commonPasswords } from "./commonPasswords"
import { KeyPasswordFeedback, PasswordResponse, scoreMax } from "./password"

const stringSchema = z.string().min(8)

const digitSchema = z.string().refine(value => /[0-9]/.test(value), {
  message: "mustContainDigit",
})

const specialCharacterSchema = z
  .string()
  .refine(value => /[^A-Za-z0-9]/.test(value), {
    message: "mustContainSpecialCharacter",
  })

const uppercaseSchema = z.string().refine(value => /[A-Z]/.test(value), {
  message: "mustContainUppercaseLetter",
})

const lowercaseSchema = z.string().refine(value => /[a-z]/.test(value), {
  message: "mustContainLowercaseLetter",
})

const commonPasswordSchema = z.string().refine(
  value => {
    return !includes(commonPasswords, value)
  },
  {
    message: "commonPassword",
  }
)

export const schemaList = [
  stringSchema,
  digitSchema,
  specialCharacterSchema,
  uppercaseSchema,
  lowercaseSchema,
  commonPasswordSchema,
]

export const getMatchFeedback = (key: KeyPasswordFeedback) => {
  const feedbackMap = {
    commonPassword: { warning: "CommonPassword", suggestions: [] },
    minLength8: {
      warning: "PatternsAreEasyToGuess",
      suggestions: ["LongerKeyboardPattern"],
    },
    mustContainDigit: {
      warning: "PatternsAreEasyToGuess",
      suggestions: ["IncludeAtLeastOneNumber", "AvoidSequences"],
    },
    mustContainSpecialCharacter: {
      warning: "PatternsAreEasyToGuess",
      suggestions: ["IncludeSymbols"],
    },
    mustContainUppercaseLetter: {
      warning: "PatternsAreEasyToGuess",
      suggestions: ["IncludeAtLeastOneUppercaseLetter"],
    },
    mustContainLowercaseLetter: {
      warning: "PatternsAreEasyToGuess",
      suggestions: ["IncludeAtLeastOneLowercaseLetter"],
    },
  }

  if (feedbackMap[key]) {
    return feedbackMap[key]
  }
  return { warning: "PatternsAreEasyToGuess", suggestions: [] }
}

export const getScore = (errors: string[]) => {
  const uniqueErrors = uniq(errors)

  if (includes(uniqueErrors, "commonPassword")) {
    return 1
  }

  let rawScore = scoreMax - uniqueErrors.length

  if (rawScore > scoreMax) {
    return scoreMax
  } else if (rawScore < 0) {
    return 0
  }

  return rawScore
}

export const validatePassword = (password: string) => {
  let keyErrors: KeyPasswordFeedback[] = []
  let response: PasswordResponse = {
    score: 0,
    feedback: {
      warning: "",
      suggestions: [],
    },
  }

  schemaList.forEach(schema => {
    const resp = schema.safeParse(password)

    if (!resp.success) {
      const msg = resp.error.issues[0].message as KeyPasswordFeedback
      const feedback = getMatchFeedback(msg as KeyPasswordFeedback)
      response.feedback.warning = feedback.warning
      response.feedback.suggestions.push(...feedback.suggestions)
      keyErrors.push(msg as KeyPasswordFeedback)
    }
  })
  response.feedback.suggestions.push("AddAnotherWord")
  response.score = getScore(keyErrors)

  if (response.score > 3) {
    response.feedback.warning = ""
    response.feedback.suggestions = []
  }

  return response
}
